import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import intersect from "@alpinejs/intersect";
import focus from "@alpinejs/focus";
import { reduce } from "lodash";
import * as shopify from "../api/shopify";
import * as rebuyApi from "../api/rebuy";
import { centsToPrice } from "../utils/filters";
import AlpineRebuyStore from "./AlpineRebuyStore";
import AlpineMapStore from "./AlpineMapStore";

const { CART, CUSTOMER_EMAIL, SHOPIFY_DOMAIN, GLOBALS } = window;

Alpine.store("main", {
  // Config
  shopifyDomain: SHOPIFY_DOMAIN,
  customerEmail: CUSTOMER_EMAIL,
  globals: GLOBALS,

  // App State
  activeProductVariantId: null,
  isMiniCartOpen: false,
  currentDropdown: null,
  isNavOpen: false,
  isMobileNavOpen: false,
  isNavigatingToCheckout: false,
  isSearchOpen: false,
  isScrolled: false,
  isNavScrolled: false,
  scrollPosition: 0,
  isNewsletterPopupOpened: false,
  footerInView: false,
  transparentHeader: false,
  showBanner: false,

  // Cart
  cart: CART || null,
  pendingUpdateVariantId: null,
  pendingAddVariantId: null,
  addedVariantIds: [],
  isCartUpdating: false,
  showGiftBag: false,

  // Blog
  activeBlogCategoryTag: null,
  activeBlogHeroImage: null,

  // Skio
  skioPurchaseOption: null,

  init() {
    this.onScroll();
    document.addEventListener("scroll", () => {
      this.onScroll();
    });

    if (location.hash) {
      addEventListener("load", () => {
        const el = document.getElementById(location.hash.replace("#", ""));
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }
      });
    }

    this.bindRebuyEvents();

    Alpine.effect(() => {
      const cart = this.cart;
      this.checkBagItem();
    });
  },

  bindRebuyEvents() {
    document.addEventListener("rebuy:cart.change", (event) => {
      this.cart = {
        ...this.cart,
        ...event.detail.cart.cart,
      };
    });
  },

  onScroll() {
    if (window.scrollY > 100) {
      this.isScrolled = true;
      if (this.scrollPosition > window.scrollY) {
        this.isNavScrolled = false;
      } else {
        this.isNavScrolled = true;
      }
      this.scrollPosition = window.scrollY;
    } else {
      this.isScrolled = false;
      this.isNavScrolled = false;
    }
  },

  toggleMobileNav() {
    this.isMobileNavOpen = !this.isMobileNavOpen;
    this.isMiniCartOpen = this.isMobileNavOpen ? false : this.isMiniCartOpen;
    this.isSearchOpen = this.isMobileNavOpen ? false : this.isSearchOpen;
  },

  setMiniCartOpen(isMiniCartOpen) {
    this.isMiniCartOpen = isMiniCartOpen;
    this.isSearchOpen = this.isMiniCartOpen ? false : this.isSearchOpen;
    this.isMobileNavOpen = this.isMiniCartOpen ? false : this.isMobileNavOpen;

    if (typeof Rebuy !== "undefined" && Rebuy?.SmartCart?.status == "ready") {
      if (this.isMiniCartOpen) {
        Rebuy.SmartCart.show();
      } else {
        Rebuy.SmartCart.hide();
      }
    } else {
      document.addEventListener("rebuy:smartcart.ready", () => {
        if (this.isMiniCartOpen) {
          Rebuy.SmartCart.show();
        } else {
          Rebuy.SmartCart.hide();
        }
      });
    }
  },

  setDesktopHeaderOpened(isNavOpen) {
    this.isNavOpen = isNavOpen;
    this.isSearchOpen = this.isNavOpen ? false : this.isSearchOpen;
    this.isMobileNavOpen = this.isNavOpen ? false : this.isMobileNavOpen;
    this.isMiniCartOpen = this.isNavOpen ? false : this.isMiniCartOpen;
  },

  closeMenus() {
    this.isNavOpen = false;
    this.isMobileNavOpen = false;
    this.isSearchOpen = false;
    this.currentDropdown = null;
  },

  removeHeaderSpacer() {
    const headerSpacer = document.querySelector(".header-spacer");
    headerSpacer?.parentNode.removeChild(headerSpacer);
  },

  setHeaderColors(background, text) {
    document.body.style.setProperty("--header-bg-color", background);
    document.body.style.setProperty("--header-text-color", text);
  },

  setActiveVariantId(activeProductVariantId) {
    this.activeProductVariantId = activeProductVariantId;

    // Set query param if we haven't already
    if (window.history && window.history.replaceState) {
      window.history.replaceState(
        {},
        window.title,
        `${window.location.pathname}?variant=${activeProductVariantId}`
      );
    }
  },

  gtagItemFromLine(lineItem, quantity = null) {
    return {
      item_id: lineItem.sku || lineItem.id,
      item_name: lineItem.product_title,
      currency: "USD",
      item_brand: lineItem.vendor,
      item_category: lineItem.product_type,
      item_variant: lineItem.title,
      discount: lineItem.total_discount ? lineItem.total_discount / 100 : 0,
      price: lineItem.final_price ? lineItem.final_price / 100 : 0,
      quantity,
    };
  },

  addVariant({
    variantId,
    selling_plan = "",
    quantity = 1,
    properties = {},
    openCart = true,
    button = null,
  }) {
    this.pendingAddVariantId = variantId;

    return shopify
      .addVariantToCart(variantId, selling_plan, quantity, properties)
      .then((response) => response.json())
      .then((body) => {
        if (body.items) {
          // ORDER HERE MATTERS
          // Shopify adds new items to the top.
          const newItems = body.items;
          const cartItems = this.cart.items;

          if (window.dataLayer) {
            const value = reduce(
              newItems,
              (totalPrice, item) => {
                return totalPrice + (item.final_price / 100 || 0);
              },
              0
            );

            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: "add_to_cart",
              ecommerce: {
                currency: "USD",
                value,
                items: newItems.map((item) =>
                  this.gtagItemFromLine(item, item.quantity)
                ),
              },
              button,
            });
          }

          // need to add the discount code to the cart checout event

          // Add back in unique cart items
          for (let i = 0; i < newItems.length; i++) {
            const newItem = newItems[i];
            const cartItem = cartItems.find((item) => item.key == newItem.key);
            if (cartItem) {
              cartItem.quantity = newItem.quantity;
            } else {
              cartItems.unshift(newItem);
            }
          }

          this.pendingAddVariantId = null;

          this.cart = {
            ...this.cart,
            ...this.calculateItems(cartItems),
          };

          Rebuy.Cart.setCart(JSON.parse(JSON.stringify(this.cart)));

          let klaviyoCartData = {
            total_price: this.cart.total_price / 100,
            $value: this.cart.total_price / 100,
            total_discount: this.cart.total_discount,
            original_total_price: this.cart.original_total_price / 100,
            items: this.cart.items,
            item: newItems[0],
          };

          // klaviyo tracking
          if (_learnq) {
            _learnq.push(["track", "Added to Cart", klaviyoCartData]);
          }

          if (openCart) {
            this.setMiniCartOpen(true);
          }
        } else if (body.description) {
          alert(body.description);
          throw new Error(body.description);
        }

        if (openCart) {
          this.setMiniCartOpen(true);
        }
      })
      .catch((error) => {
        this.pendingAddVariantId = null;
        throw error;
      });
  },

  updateVariantQuantity(variantId, quantity) {
    this.pendingUpdateVariantId = variantId;

    return shopify
      .updateVariantQuantity(variantId, quantity)
      .then((response) => response.json())
      .then((cart) => {
        this.pendingUpdateVariantId = null;
        this.cart = cart;
        Rebuy.Cart.setCart(JSON.parse(JSON.stringify(this.cart)));
      })
      .catch((error) => {
        this.pendingUpdateVariantId = null;
        throw error;
      });
  },

  async navigateToCheckout() {
    this.isNavigatingToCheckout = true;

    await this.fetchCart();

    const checkoutUrl = "/checkout";

    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          currency: "USD",
          value: this.cart.total_price / 100,
          checkout: {
            items: this.cart.items.map((item) =>
              this.gtagItemFromLine(item, item.quantity)
            ),
          },
        },
      });
    }

    window.location = checkoutUrl;
    this.isNavigatingToCheckout = false;
  },

  setSearchOpen(isSearchOpen) {
    this.isSearchOpen = isSearchOpen;
    this.isMobileNavOpen = isSearchOpen ? false : this.isMobileNavOpen;
    this.isMiniCartOpen = isSearchOpen ? false : this.isMiniCartOpen;
    this.isNavOpen = isSearchOpen ? false : this.isNavOpen;
  },

  centsToPrice(cents) {
    return centsToPrice(cents);
  },

  fetchCart() {
    this.isCartUpdating = true;

    return shopify
      .fetchCart()
      .then((response) => response.json())
      .then((cart) => {
        this.isCartUpdating = false;
        this.cart = cart;
        Rebuy.Cart.setCart(JSON.parse(JSON.stringify(this.cart)));
      })
      .catch((error) => {
        this.isCartUpdating = false;
        throw error;
      });
  },

  async updateCartAttributes(attributes) {
    this.isCartUpdating = true;

    const oldAttributes = this.cart.attributes;

    const newAttributes = {
      ...this.cart.attributes,
      ...attributes,
    };

    this.cart.attributes = newAttributes;

    try {
      const response = await shopify.updateCartAttributes(newAttributes);
      const cart = await response.json();
      this.isCartUpdating = false;
      this.cart = cart;
      Rebuy.Cart.setCart(JSON.parse(JSON.stringify(this.cart)));
    } catch (error) {
      console.error(error);
      this.cart.attributes = oldAttributes;
      this.isCartUpdating = false;
    }
  },

  calculateItems(items) {
    const item_count = items.reduce(
      (quantity, item) => quantity + (item.quantity || 0),
      0
    );

    const total_price = items.reduce((totalPrice, item) => {
      return totalPrice + (item.discounted_price || 0) * item.quantity;
    }, 0);

    return { item_count, total_price };
  },

  graphQl(query) {
    return shopify.graphQl(query);
  },

  search(query, resources = {}) {
    return shopify.search(query, resources);
  },

  storeRecentProduct(handle) {
    let recentProductIds;

    try {
      recentProductIds = JSON.parse(Cookies.get("recentProductIds"));
    } catch {
      recentProductIds = [];
    }

    if (!recentProductIds?.includes(handle)) {
      recentProductIds = [handle, ...recentProductIds].slice(0, 11);
      Cookies.set("recentProductIds", JSON.stringify(recentProductIds), {
        expires: 30,
      });
    }
  },

  rebuyGetProductsFromRuleset(rulesetId) {
    return rebuyApi.getProductsFromRuleset(rulesetId);
  },

  checkBagItem() {
    const bagItem = this.cart.items.find((item) => item.id == 44565318861050);
    this.showGiftBag = !bagItem;
  },

  addGiftBag() {
    if (this.pendingAddVariantId == 44565318861050) {
      return;
    }

    const bagItem = this.cart.items.find((item) => item.id == 44565318861050);
    if (!bagItem) {
      return this.addVariant({
        variantId: 44565318861050,
        openCart: false,
      });
    }
  },
});

Alpine.data("select", () => {
  return {
    init() {
      this.$nextTick(() => {
        new Choices(this.$refs.select, {
          searchEnabled: false,
          itemSelectText: "",
          placeholder: true,
        });
      });
    },
  };
});

Alpine.data("productForm", (variants, initialActiveVariantId, isPDP) => {
  function checkForQueryParam() {
    let variantId;
    const urlParams = new URLSearchParams(location.search);
    for (const [key, value] of urlParams) {
      if (key === "variant" && value) {
        variantId = value;
      }
    }
    return variantId;
  }

  const updatedInitialActiveVariantId =
    checkForQueryParam() || initialActiveVariantId;

  const initialActiveVariant = variants.find(
    (variant) =>
      variant.id ==
      (isPDP ? updatedInitialActiveVariantId : initialActiveVariantId)
  );

  if (isPDP) {
    Alpine.store("main").setActiveVariantId(initialActiveVariant.id);
  }

  const reactiveData = Alpine.reactive({
    activeVariant: initialActiveVariant,
  });

  Alpine.effect(() => {
    const activeProductVariantId = Alpine.store("main").activeProductVariantId;

    if (isPDP) {
      const newActiveVariant = variants.find(
        (variant) => variant.id == activeProductVariantId
      );
      if (newActiveVariant) {
        reactiveData.activeVariant = newActiveVariant;
        document.dispatchEvent(
          new CustomEvent("variantChanged", {
            detail: { variantId: newActiveVariant.id },
          })
        );
      }
    }
  });

  return {
    isOptionValueSelected(optionIndex, value) {
      optionIndex = parseInt(optionIndex.replace("option", "")) - 1;
      return (
        this.reactiveData.activeVariant?.optionValues[optionIndex] == value
      );
    },
    getVariant(optionIndex, value) {
      optionIndex = parseInt(optionIndex.replace("option", "")) - 1;
      const optionValues = [...this.reactiveData.activeVariant.optionValues];
      optionValues[optionIndex] = value;
      return this.variants.find(
        (variant) =>
          JSON.stringify(variant.optionValues) == JSON.stringify(optionValues)
      );
    },
    isOptionAvailable(optionIndex, value) {
      const variant = this.getVariant(optionIndex, value);

      if (variant) {
        return variant.isAvailable;
      }
      return false;
    },
    onOptionChanged(optionIndex, value) {
      const variant = this.getVariant(optionIndex, value);
      if (variant) {
        if (isPDP) {
          Alpine.store("main").setActiveVariantId(variant.id);
        }
        this.reactiveData.activeVariant = variant;
      }
    },
    changeActiveVariant(newVariantId) {
      const newActiveVariant = this.variants.find(
        (variant) => variant.id == newVariantId
      );
      if (newActiveVariant && isPDP) {
        Alpine.store("main").setActiveVariantId(newActiveVariant.id);
      }
      this.reactiveData.activeVariant = newActiveVariant;
    },
    incrementQuantity() {
      const { inventoryPolicy, inventory } = this.reactiveData.activeVariant;
      const sellPastAvailable = inventoryPolicy == "continue";
      if (!sellPastAvailable && this.quantity >= inventory) return;
      this.quantity++;
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    incrementDisabled() {
      const { inventoryPolicy, inventory } = this.reactiveData.activeVariant;
      const sellPastAvailable = inventoryPolicy == "continue";
      return !sellPastAvailable && this.quantity >= inventory;
    },
    reactiveData,
    variants,
    quantity: 1,
  };
});

Alpine.directive("price", (el, { expression }, { evaluateLater, effect }) => {
  let getCents = evaluateLater(expression);

  effect(() => {
    getCents((cents) => {
      el.innerHTML = centsToPrice(cents);
    });
  });
});

Alpine.plugin(collapse);
Alpine.plugin(intersect);
Alpine.plugin(focus);

window.Alpine = Alpine;
window.Alpine.start();
